<template>
    <div class="modal fade" id="vehicleSelectorModal" ref="modalRef" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title fw-bold">
                        <i class="bi bi-car-front-fill me-2 text-primary"></i>Wybierz pojazd z bazy
                    </h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body">
                    <!-- WYSZUKIWARKA -->
                    <div class="mb-3">
                        <div class="input-group">
                            <span class="input-group-text bg-light">
                                <i class="bi bi-search"></i>
                            </span>
                            <input type="text" class="form-control" v-model="searchQuery" @input="onSearchInput"
                                placeholder="Szukaj po marce, modelu, VIN lub numerze rejestracyjnym..." />
                            <button v-if="searchQuery" class="btn btn-outline-secondary" type="button"
                                @click="clearSearch">
                                <i class="bi bi-x-lg"></i>
                            </button>
                        </div>
                    </div>

                    <!-- TABELA POJAZDÓW -->
                    <div class="table-responsive">
                        <table class="table table-hover align-middle">
                            <thead class="table-light">
                                <tr>
                                    <th style="width: 70px;">Zdjęcie</th>
                                    <th>Marka / Model</th>
                                    <th>VIN</th>
                                    <th>Nr rejestracyjny</th>
                                    <th>Rok prod.</th>
                                    <th>Cena brutto</th>
                                    <th class="text-end" style="width: 120px;">Akcja</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="loading">
                                    <td colspan="7" class="text-center py-4">
                                        <div class="spinner-border spinner-border-sm text-primary me-2" role="status">
                                        </div>
                                        <span>Wczytywanie listy pojazdów...</span>
                                    </td>
                                </tr>
                                <tr v-else-if="vehicles.length === 0">
                                    <td colspan="7" class="text-center text-muted py-4">
                                        Brak pojazdów spełniających kryteria wyszukiwania.
                                    </td>
                                </tr>
                                <tr v-else v-for="v in vehicles" :key="v.ID">
                                    <td>
                                        <div style="width: 50px; height: 38px;" class="position-relative">
                                            <!-- Placeholder zawsze pod spodem jako tło -->
                                            <div
                                                class="rounded border bg-light text-muted d-flex align-items-center justify-content-center w-100 h-100">
                                                <i class="bi bi-car-front fs-6"></i>
                                            </div>

                                            <!-- Obrazek nakładany na wierzch (jeśli istnieje) -->
                                            <img v-if="v.image"
                                                :src="`https://integrator.wrealizacji.pl/photos/${v.ID}/thumb/${v.image}`"
                                                :alt="`${v.make} ${v.model}`"
                                                class="position-absolute top-0 start-0 rounded object-fit-cover w-100 h-100 border cursor-pointer"
                                                @error="(e) => e.target.style.display = 'none'" />
                                        </div>
                                    </td>
                                    <td>
                                        <div class="fw-semibold">{{ v.make }} {{ v.model }}</div>
                                        <small class="text-muted" v-if="v.engineCCM">{{ v.engineCCM }} cm³</small>
                                    </td>
                                    <td><code class="text-dark">{{ v.VIN }}</code></td>
                                    <td><span class="badge bg-light text-dark border">{{ v.registerNumber || 'Brak'
                                    }}</span></td>
                                    <td>{{ v.productionYear }}</td>
                                    <td class="fw-bold text-success">{{ formatPrice(v.price) }} PLN</td>
                                    <td class="text-end">
                                        <button type="button" class="btn btn-sm btn-primary" @click="selectVehicle(v)">
                                            Wybierz
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import Modal from 'bootstrap/js/dist/modal'
import apiClient from '@/api'

const emit = defineEmits(['select'])

const modalRef = ref(null)
let modalInstance = null

const loading = ref(false)
const vehicles = ref([])
const searchQuery = ref('')
let searchTimeout = null

const formatPrice = (val) => {
    if (!val) return '0.00'
    return Number(val).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}


// Pobieranie aut przez action: 'get-cars'
const fetchVehicles = async () => {
    loading.value = true
    try {
        const response = await apiClient.post(
            {
                action: 'get-cars',
                method: 'post',
                page: 1,
                limit: 50,
                search: searchQuery.value,
                active: '',
                makeID: null,
                modelID: null,
                sortBy: null,
                sortType: null
            }
        )
        // Odczyt z response.data.data
        vehicles.value = response.data?.data || []
    } catch (e) {
        console.error('Błąd pobierania pojazdów:', e)
    } finally {
        loading.value = false
    }
}

const onSearchInput = () => {
    clearTimeout(searchTimeout)
    searchTimeout = setTimeout(() => {
        fetchVehicles()
    }, 400)
}

const clearSearch = () => {
    searchQuery.value = ''
    fetchVehicles()
}

const show = () => {
    fetchVehicles()
    modalInstance?.show()
}

const hide = () => {
    modalInstance?.hide()
}

// Mapujemy oryginalne klucze z bazy na strukturę oczekiwaną w formularzu
const selectVehicle = (v) => {
    const mappedVehicle = {
        id: v.ID,
        make: v.make || '',
        model: v.model || '',
        engineCapacity: v.engineCCM || '',
        mileage: v.mileage || '',
        gearbox: v.gearboxTXT || v.gearbox || '',
        fuelType: v.fuelTXT || v.fuelType || '',
        powerHp: v.powerKM || '',
        color: v.colourTXT || v.colour || '',
        vin: v.VIN || '',
        registrationPlate: v.registerNumber || '',
        productionYear: v.productionYear || '',
        firstRegistrationDate: v.firstRegistrationDate || '',
        additionalInfo: '',
        price: v.price || 0
    }

    emit('select', mappedVehicle)
    hide()
}

onMounted(() => {
    modalInstance = new Modal(modalRef.value)
})

defineExpose({ show, hide })
</script>

<style scoped>
.object-fit-cover {
    object-fit: cover;
}
</style>